<template>
  <div
    class="sp-progress"
    role="progressbar"
    :class="classModifiers"
    :style="style"
    :aria-label="ariaLabel"
    :aria-valuenow="ariaValueNow"
    :aria-valuemin="ariaValueMin"
    :aria-valuemax="ariaValueMax"
    :aria-busy="ariaBusy"
  >
    <component :is="componentMap[type]" :value="value" />
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent } from "vue";
import { props as progressProps } from "./props.js";
import { sizePropValues } from "#ce/composables/useSize.js";
import { useColorToCssProperty } from "#ce/composables/useColor";
import { isCssUnit } from "#ce/utils/css";
import { toBoolean } from "#ce/utils/props";
import { useSpProgress } from "./composables/useSpProgress";

const componentMap = {
  circular: defineAsyncComponent(() => import("./components/ProgressCircular.ce.vue")),
  linear: defineAsyncComponent(() => import("./components/ProgressLinear.ce.vue")),
  ellipsis: defineAsyncComponent(() => import("./components/ProgressEllipsis.ce.vue")),
};

const props = defineProps(progressProps);

const { isDeterminate, clampedValue } = useSpProgress(props);

// Use color composable to map system colors to CSS variables
useColorToCssProperty(() => props.color, { cssProperty: "progress-color" });

const classModifiers = computed(() => ({
  [`--type-${props.type}`]: true,
  [`--size-${props.size}`]: sizePropValues.includes(props.size),
}));

// Apply custom size if it's a CSS unit (e.g., "2rem", "24px");
// when linear and not expanded, set width in pixels
const style = computed(() => {
  const base = {};
  if (isCssUnit(props.size)) {
    base["--custom-size"] = props.size;
  }
  if (props.type === "linear" && !toBoolean(props.expanded)) {
    base.width = `${props.width}px`;
  }
  return base;
});

const ariaValueNow = computed(() => (isDeterminate.value ? clampedValue.value : undefined));
const ariaValueMin = computed(() => (isDeterminate.value ? 0 : undefined));
const ariaValueMax = computed(() => (isDeterminate.value ? 100 : undefined));
const ariaBusy = computed(() => !isDeterminate.value);
</script>

<style lang="scss">
:host {
  display: block;

  // Size tokens
  --progress-size-micro: var(--sp-comp-progress-size-micro, var(--sp-ref-scale-4));
  --progress-size-xxx-small: var(--sp-comp-progress-size-xxx-small, var(--sp-ref-scale-6));
  --progress-size-xx-small: var(--sp-comp-progress-size-xx-small, var(--sp-ref-scale-8));
  --progress-size-x-small: var(--sp-comp-progress-size-x-small, var(--sp-ref-scale-12));
  --progress-size-small: var(--sp-comp-progress-size-small, var(--sp-ref-scale-16));
  --progress-size-medium: var(--sp-comp-progress-size-medium, var(--sp-ref-scale-24));
  --progress-size-large: var(--sp-comp-progress-size-large, var(--sp-ref-scale-32));
  --progress-size-x-large: var(--sp-comp-progress-size-x-large, var(--sp-ref-scale-40));
  --progress-size-xx-large: var(--sp-comp-progress-size-xx-large, var(--sp-ref-scale-48));
  --progress-size-xxx-large: var(--sp-comp-progress-size-xxx-large, var(--sp-ref-scale-56));
  --progress-size-display: var(--sp-comp-progress-size-display, var(--sp-ref-scale-64));
  --progress-size-x-display: var(--sp-comp-progress-size-x-display, var(--sp-ref-scale-72));

  // Size + color tokens
  --progress-size: var(--progress-size-medium);
  --progress-color: var(--sp-comp-progress-color, var(--sp-sys-color-primary));
  --progress-track-color: var(--sp-comp-progress-track-color, var(--sp-sys-color-outline-variant));

  // Circular
  --progress-circular-stroke-width: var(--sp-comp-progress-circular-stroke-width, 4);

  // Linear
  --progress-linear-border-radius: var(--sp-comp-progress-linear-border-radius, calc(var(--progress-size) / 2));

  // Motion
  --progress-transition-duration: var(--sp-comp-progress-transition-duration, 0.3s);
  --progress-transition-easing: var(--sp-comp-progress-transition-easing, ease-in-out);
  --progress-indeterminate-anim-duration: var(--sp-comp-progress-indeterminate-anim-duration, 1.4s);
}
</style>

<style scoped lang="scss">
.sp-progress {
  position: relative;

  $sizes: (
    micro: var(--progress-size-micro),
    xxx-small: var(--progress-size-xxx-small),
    xx-small: var(--progress-size-xx-small),
    x-small: var(--progress-size-x-small),
    small: var(--progress-size-small),
    medium: var(--progress-size-medium),
    large: var(--progress-size-large),
    x-large: var(--progress-size-x-large),
    xx-large: var(--progress-size-xx-large),
    xxx-large: var(--progress-size-xxx-large),
    display: var(--progress-size-display),
    x-display: var(--progress-size-x-display),
  );

  // Custom size support
  &[style*="--custom-size"] {
    --progress-size: var(--custom-size);
  }

  // Size modifiers
  @each $name, $size in $sizes {
    &.--size-#{$name} {
      --progress-size: #{$size};
    }
  }

  &.--type-linear,
  &.--type-ellipsis {
    width: 100%;
    max-width: 100%;
    min-width: 0; // Allow flex shrink without overflow
    height: var(--progress-size);
    display: block;
    flex: 1 1 auto; // Allow it to grow within flex container
  }
}
</style>
